    import { ENV } from "@/core/config/env";
import { slugify } from "@/lib/slugify";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { FilterSection } from "../ui/FilterSection";
import { GET_CATEGORY_BY_PARENTID } from "@/core/graphql/queries/getCategories";
import { useLazyQuery, useQuery } from "@apollo/client";
import { DEFAULT_PAGE_SIZE, SALES_TYPES, SalesType, Scope, SCOPES } from "@/core/constants";
import { Pagination } from "../shared/Pagination";
import { Category } from "@/core/models/categories/category";
import Loading from "../shared/Loading";
import { ProductItem } from "@/core/models/products/productList";
import { SearchProductRequest } from "@/core/models/products/productRequest";
import { GET_PRODUCTS } from "@/core/graphql/queries/getProductList";
import { formatDate } from "@/lib/format";
import PurchaseProduct from "./PurchaseProduct";
import BidProduct from "./BidProduct";
import HireProduct from "./HireProduct";
import { PaginationInfo } from "../shared/PaginationInfo";
import { NoRecordsCard } from "../ui/NoRecordsCard";
import { useCompare } from "@/contexts/CompareContext";
import CompareModal from "./CompareModal";
import { trackViewItemList } from "@/lib/gtm";


type FilterState = {
    searchText?: string;
    category?: number[];
    scope?: number;
    saleType?: number;
    page: number;
    size?: number;
};

interface ProductListProps {
    searchParams: {
        category?: string;
        scope?: string;
        saleType?: string;
        text?: string;
        page?: number;
    },
    initialProducts: ProductItem[];
    initialPagination: {
        count: number;
        currentPage: number;
        totalPages: number;
    };
}
export default function ProductList({
    searchParams,
    initialProducts,
    initialPagination,
}: ProductListProps) {
    const [products, setProducts] = useState<ProductItem[]>(initialProducts || []);
    const [pagination, setPagination] = useState(initialPagination);
    const { compareProducts, setCompareOpen, clearCompare } = useCompare();

    const [filters, setFilters] = useState<FilterState>({
        searchText: searchParams.text || "",
        page: searchParams.page ?? 1,
        size: DEFAULT_PAGE_SIZE,
    });

    const { data: categoryRes } = useQuery(GET_CATEGORY_BY_PARENTID, {
        variables: { id: Number(ENV.CATEGORY_ID), size: 1000 },
    });

    const categories: Category[] = categoryRes?.getMstCategoryByParentId?.result ?? [];

    // Child categories loaded lazily on user expansion — supports infinite depth
    const [expandedCatIds, setExpandedCatIds] = useState<Set<number>>(new Set());
    const [childCatsMap, setChildCatsMap] = useState<Record<number, Category[]>>({});
    const [childCatsLoading, setChildCatsLoading] = useState<Set<number>>(new Set());

    const [fetchChildCategories] = useLazyQuery(GET_CATEGORY_BY_PARENTID, {
        fetchPolicy: 'cache-first',
    });

    // Flat list: parents + all loaded children (for label resolution & slug matching)
    const allCategories: Category[] = [
        ...categories,
        ...Object.values(childCatsMap).flat(),
    ];

    const handleExpandCategory = (catId: number) => {
        setExpandedCatIds(prev => {
            const next = new Set(prev);
            if (next.has(catId)) { next.delete(catId); } else { next.add(catId); }
            return next;
        });
        // Only fetch if we haven't fetched this node yet
        if (!Object.prototype.hasOwnProperty.call(childCatsMap, catId)) {
            setChildCatsLoading(prev => new Set(prev).add(catId));
            fetchChildCategories({ variables: { id: catId, size: 1000 } }).then(({ data }) => {
                const result: Category[] = data?.getMstCategoryByParentId?.result ?? [];
                setChildCatsMap(prev => ({ ...prev, [catId]: result }));
                setChildCatsLoading(prev => { const next = new Set(prev); next.delete(catId); return next; });
            });
        }
    };

    const getSelectedCategoryNames = (
        selected: number[] | number | undefined,
        allCategories: Category[]
    ): string[] => {
        if (selected === undefined) return [];

        const selectedIds = Array.isArray(selected) ? selected : [selected];

        return allCategories
            .filter((cat) => selectedIds.includes(cat.categoryId))
            .map((cat) => cat.categoryName);
    };
    
    const selectedCategoryNames = getSelectedCategoryNames(filters.category, allCategories);

    const categoryLabel: string = selectedCategoryNames.length > 0
        ? `${selectedCategoryNames.join(", ")} Products`
        : "Products";

    const buildVariables = (state: FilterState): SearchProductRequest => ({
        domainCategoryIds: state.category?.join(",") || String(ENV.CATEGORY_ID),
        salesTypeId: state.saleType || null,
        scopeId: state.scope || null,
        page: state.page,
        size: state.size ?? DEFAULT_PAGE_SIZE,
    });

    const hasInitial = (initialProducts?.length ?? 0) > 0;

    const [fetchProducts, { data, loading, refetch }] = useLazyQuery(GET_PRODUCTS, {
        fetchPolicy: "network-only",
    });

    useEffect(() => {
        // Only run if data is available
        if (categories.length) {
            const newFilters: Partial<FilterState> = {
                ...filters,
            };

            if (searchParams.category) {
                const matchedCategory: Category | undefined = allCategories.find((c: Category) =>
                    slugify(c.categoryName) === slugify(searchParams.category!)
                );
                if (matchedCategory) {
                    newFilters.category = [Number(matchedCategory.categoryId)];
                }
            }

            if (searchParams.scope) {
                const matchedScope: Scope | undefined = SCOPES.find((s: Scope) =>
                    slugify(s.scopeName) === slugify(searchParams.scope!)
                );
                if (matchedScope) {
                    newFilters.scope = Number(matchedScope.scopeId);
                }
            }

            if (searchParams.saleType) {
                const matchedSaleType: SalesType | undefined = SALES_TYPES.find((s: SalesType) =>
                    slugify(s.saleTypeName) === slugify(searchParams.saleType!)
                );
                if (matchedSaleType) {
                    newFilters.saleType = Number(matchedSaleType.saleTypeId);
                }
            }


            // Set updated filters and page
            setFilters({
                ...newFilters,
                page: searchParams.page ?? 1,
                searchText: searchParams.text || "",
                size: DEFAULT_PAGE_SIZE,
            });
        }
    }, [searchParams, categories]);

    useEffect(() => {
        if (data?.getPrdProductList) {
            // Process products to ensure all have a ratingScore
            const processedProducts = (data.getPrdProductList.result ?? []).map((product: ProductItem) => {
                // Generate random rating if not provided (between 3.5 and 5.0 for realistic ratings)
                const rating = product.ratingScore && product.ratingScore > 0 
                    ? product.ratingScore 
                    : Math.round((Math.random() * 1.5 + 3.5) * 10) / 10;

                return {
                    ...product,
                    ratingScore: rating,
                };
            });

            setProducts(processedProducts);
            setPagination({
                count: data.getPrdProductList.count ?? 0,
                currentPage: data.getPrdProductList.currentPage ?? 1,
                totalPages: data.getPrdProductList.totalPages ?? 0,
            });

            // GA4 eCommerce: view_item_list
            if (processedProducts.length > 0) {
                trackViewItemList(processedProducts, 'product_list', categoryLabel);
            }
        }
    }, [data]);

    // Show loader only when actively fetching without initial data
    const showLoading = loading && !hasInitial;
    if (showLoading) return <Loading />;

    const handleFilterChange = (field: keyof FilterState, value: number[] | number) => {
        setFilters((prev) => {
            const next: FilterState = {
                ...prev,
                [field]: value as never,
                page: 1,
            };
            // Trigger fetch only on user interaction
            fetchProducts({ variables: buildVariables(next) });
            return next;
        });
    };

    const handleSearch = (value: string) => {
        setFilters((prev) => {
            const next: FilterState = {
                ...prev,
                searchText: value,
                page: 1,
            };
            fetchProducts({ variables: buildVariables(next) });
            return next;
        });
    };

    const handlePageChange = (newPage: number) => {
        setFilters((prev) => {
            const next: FilterState = {
                ...prev,
                page: newPage,
            };
            fetchProducts({ variables: buildVariables(next) });
            return next;
        });
    };

    // Generate SEO-friendly pagination URLs
    const generatePaginationUrl = (page: number): string => {
        const queryParams = new URLSearchParams();

        if (filters.searchText) {
            queryParams.append('text', filters.searchText);
        }

        if (filters.category && filters.category.length > 0) {
            const categoryNames = allCategories
                .filter((c: Category) => filters.category!.includes(c.categoryId))
                .map((c: Category) => slugify(c.categoryName))
                .join(',');
            queryParams.append('category', categoryNames);
        }

        if (filters.scope) {
            const scopeName = SCOPES.find((s: Scope) => s.scopeId === filters.scope);
            if (scopeName) {
                queryParams.append('scope', slugify(scopeName.scopeName));
            }
        }

        if (filters.saleType) {
            const saleTypeName = SALES_TYPES.find((s: SalesType) => s.saleTypeId === filters.saleType);
            if (saleTypeName) {
                queryParams.append('saleType', slugify(saleTypeName.saleTypeName));
            }
        }

        if (page > 1) {
            queryParams.append('page', page.toString());
        }

        const queryString = queryParams.toString();
        return `${ENV.DOMAIN_URL}/product${queryString ? `?${queryString}` : ''}`;
    };

    // Recursive category node renderer — drills down until no children are returned
    const renderCategoryNode = (cat: Category): React.ReactNode => {
        const isSelected = filters.category?.includes(cat.categoryId);
        const isExpanded = expandedCatIds.has(cat.categoryId);
        const children = childCatsMap[cat.categoryId];          // undefined = not yet fetched
        const isLoading = childCatsLoading.has(cat.categoryId);
        const hasFetched = Object.prototype.hasOwnProperty.call(childCatsMap, cat.categoryId);
        // Hide arrow only after we've fetched and confirmed there are no children
        const showArrow = !hasFetched || (children && children.length > 0);

        return (
            <div key={cat.categoryId}>
                <div className="flex items-center justify-between pr-1">
                    <button
                        type="button"
                        onClick={() => handleFilterChange("category", [cat.categoryId])}
                        className={`flex-1 text-left px-2 py-1.5 text-sm rounded transition-colors ${
                            isSelected ? 'font-semibold text-primary' : 'text-gray-700 hover:text-primary'
                        }`}
                    >
                        {cat.categoryName}
                    </button>
                    {showArrow && (
                        <button
                            type="button"
                            onClick={() => handleExpandCategory(cat.categoryId)}
                            aria-label={isExpanded ? 'Collapse subcategories' : 'Expand subcategories'}
                            className="p-1 text-gray-400 hover:text-gray-600"
                        >
                            <svg
                                className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                    )}
                </div>
                {isExpanded && (
                    <div className="ml-3 border-l border-gray-200 pl-2 mb-1">
                        {isLoading ? (
                            <p className="text-xs text-gray-400 px-2 py-1">Loading...</p>
                        ) : children && children.length > 0 ? (
                            children.map((child: Category) => renderCategoryNode(child))
                        ) : null}
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <div className="flex flex-wrap lg:gap-6 lg:flex-nowrap my-10">

                {/* Left Filter Panel */}
                <div className="basis-12/12 lg:basis-4/12 xl:basis-3/12">
                    <div className="py-3 px-5 text-white bg-secondary flex justify-between items-center">
                        <p className="text-lg">Filter Option</p>
                    </div>
                    <div className="border border-gray-300">
                        {/* Search */}
                        <div className="border-b border-gray-300">
                            <div className="bg-white px-4 py-3 flex justify-between items-center cursor-pointer">
                                <p className="text-md font-semibold">Search Product</p>
                                <FontAwesomeIcon icon={faAngleDown} className="text-lg" />
                            </div>
                            <div className="bg-gray-50 px-4 py-5">
                                <form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        const input = e.currentTarget.elements.namedItem("search") as HTMLInputElement;
                                        handleSearch(input.value);
                                    }}
                                >
                                    <div className="relative">
                                        <input
                                            type="search"
                                            name="search"
                                            placeholder="Enter Product name"
                                            defaultValue={filters.searchText}
                                            className="form-control border border-gray-300 text-sm w-full h-10 px-5"
                                        />
                                        <button
                                            type="submit"
                                            className="absolute top-[50%] translate-y-[-50%] right-0 h-10 px-4 bg-primary text-white"
                                        >
                                            Search
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                        {/* Categories – infinite-depth lazy tree */}
                        <div className="border-b border-gray-300">
                            <div className="bg-white px-4 py-3 flex justify-between items-center">
                                <p className="text-md font-semibold">Categories</p>
                                <FontAwesomeIcon icon={faAngleDown} className="text-lg" />
                            </div>
                            <div className="bg-gray-50 px-2 py-2 max-h-72 overflow-y-auto">
                                {categories.map((cat: Category) => renderCategoryNode(cat))}
                            </div>
                        </div>
                        <FilterSection
                            title="Scope"
                            data={SCOPES}
                            selected={filters.scope ? [filters.scope] : []}
                            onChange={(values) => handleFilterChange("scope", values[0])}
                            labelKey="scopeName"
                            valueKey="scopeId"
                            mode="single"
                        />
                        <FilterSection
                            title="Sales Type"
                            data={SALES_TYPES}
                            selected={filters.saleType ? [filters.saleType] : []}
                            onChange={(values) => handleFilterChange("saleType", values[0])}
                            labelKey="saleTypeName"
                            valueKey="saleTypeId"
                            mode="single"
                        />
                    </div>
                </div>

                {/* Right Business Listing */}
                <div className="basis-12/12 lg:basis-8/12 xl:basis-9/12 mt-5 lg:mt-0">

                    {pagination && (
                        <PaginationInfo
                            currentPage={pagination.currentPage}
                            pageSize={DEFAULT_PAGE_SIZE}
                            totalCount={pagination.count}
                            label={categoryLabel}
                        />
                    )}

                    {/* Product Cards */}
                    {products.length === 0 && (<NoRecordsCard />)}
                    <div className="flex flex-wrap gap-y-5 -mx-[12px]">
                        {products.map((product, index) => (
                            <div className="basis-12/12 min-[550px]:basis-6/12 xl:basis-4/12 px-[12px] h-full" key={index}>
                                <div className="flex flex-col h-full relative">
                                    {/* date box */}
                                    <div className="w-[100px] text-center uppercase absolute max-[451px]:-right-5 -right-4 top-10 z-10">
                                        <div className="bg-primary text-white py-1 px-2 relative">
                                            <span className="text-[12px] font-semibold">
                                                {formatDate(product.endDate || "")}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Product Type */}
                                    {product.salesTypeId === 1 && <PurchaseProduct product={product} />}
                                    {product.salesTypeId === 2 && <BidProduct product={product} refetchOnSuccess={refetch} />}
                                    {product.salesTypeId === 3 && <HireProduct product={product} refetchOnSuccess={refetch} />}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    <div className="flex flex-col md:flex-row mt-10 justify-center">
                        {pagination && (
                            <Pagination
                                currentPage={pagination.currentPage}
                                totalPages={pagination.totalPages}
                                onPageChange={handlePageChange}
                                generateUrl={generatePaginationUrl}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Floating Compare Bar */}
            {compareProducts.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 z-[1000] bg-gradient-to-r from-blue-600 to-blue-700 shadow-2xl">
                    <div className="container py-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <p className="text-white font-semibold text-lg">
                                    Compare Products: {compareProducts.length}/4
                                </p>
                                <div className="hidden md:flex gap-2">
                                    {compareProducts.slice(0, 3).map((product) => (
                                        <div key={product.productID} className="bg-white/20 backdrop-blur-sm rounded px-3 py-1 text-white text-sm">
                                            {product.productName.substring(0, 30)}...
                                        </div>
                                    ))}
                                    {compareProducts.length > 3 && (
                                        <div className="bg-white/20 backdrop-blur-sm rounded px-3 py-1 text-white text-sm">
                                            +{compareProducts.length - 3} more
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setCompareOpen(true)}
                                    disabled={compareProducts.length < 2}
                                    className="px-6 py-2 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Compare Now
                                </button>
                                <button
                                    onClick={clearCompare}
                                    className="px-4 py-2 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
                                >
                                    Clear All
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Compare Modal */}
            <CompareModal />
        </>
    );
}